import { renderEmbeddedEntry, renderText } from "@2u-landing-pages/utils/contentful-richtext";
import EventClient from "@2u-landing-pages/utils/event-client";
import { useContentfulInspectorMode } from "@contentful/live-preview/react";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import {
  AccordionBody,
  AccordionContext,
  AccordionHeader,
  Body,
  AccordionItem as BricksAccordionItem,
  Heading,
} from "@red-digital/bricks";
import cn from "classnames";
import React, { useContext, useMemo } from "react";
import type { AccordionWrapperProps } from "./Accordion.types";
// @ts-ignore
import styles from "./Accordion.module.scss";

export const AccordionWrapper: React.FC<AccordionWrapperProps> = ({
  sectionName,
  position,
  item,
  itemKey,
  style,
  orientation,
  phoneNumber,
  parseMetadata,
}) => {
  const { expandedItems } = useContext(AccordionContext);

  const inspector = useContentfulInspectorMode();
  const labelText = item?.fields?.label;
  const headingText = item?.fields?.heading;
  const label = useMemo(() => {
    return labelText ? renderText(parseMetadata ? parseMetadata(labelText) : labelText, phoneNumber).toString() : "";
  }, [labelText, parseMetadata, phoneNumber]);
  const heading = useMemo(() => {
    return headingText
      ? renderText(parseMetadata ? parseMetadata(headingText) : headingText, phoneNumber).toString()
      : "";
  }, [headingText, parseMetadata, phoneNumber]);

  const handleOnClick = () => {
    const isExpanded = expandedItems.includes(itemKey);

    EventClient.elementClicked({
      webElement: {
        elementType: "Landing Page",
        htmlId: window.location.href,
        location: sectionName,
        name: isExpanded ? "CLOSE" : "OPEN",
        position: `${position}`,
        text: item?.fields?.heading || "",
      },
    });
  };

  const handleExternalLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    EventClient.elementClicked({
      webElement: {
        elementType: "Landing Page",
        htmlId: event.currentTarget.href,
        location: "Accordion",
        name: event.currentTarget.innerText,
        position: "",
        text: "",
      },
    });
  };

  return (
    <div
      className={cn(styles.wrapper, {
        [styles.wrapperExpanded]: expandedItems.includes(itemKey),
        [styles.wrapperHorizontal]: orientation === "horizontal",
      })}
      style={style}
    >
      {item?.fields?.label && orientation === "vertical" && (
        <Heading
          type="h2"
          variant="heading-md"
          className={styles.label}
          {...inspector({
            entryId: item.sys.id,
            fieldId: "label",
          })}
        >
          {label}
        </Heading>
      )}
      <BricksAccordionItem className={styles.item} itemKey={itemKey} disabled={!item?.fields?.body}>
        <AccordionHeader
          className={cn({
            [styles.headerDisabled]: !item?.fields?.body,
          })}
          {...inspector({
            entryId: item?.sys?.id,
            fieldId: "heading",
          })}
          onClick={handleOnClick}
        >
          {heading}
        </AccordionHeader>
        {item?.fields?.body ? (
          <AccordionBody
            className={styles.body}
            {...inspector({
              entryId: item.sys.id,
              fieldId: "body",
            })}
          >
            {documentToReactComponents(item?.fields?.body, {
              renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
              renderNode: {
                [BLOCKS.PARAGRAPH]: (node, children) => (
                  <Body type="p" variant="body-md" modifier={500}>
                    {children}
                  </Body>
                ),
                [INLINES.EMBEDDED_ENTRY]: (node) =>
                  renderEmbeddedEntry(node, "Accordion", item?.fields?.body, phoneNumber, parseMetadata),
                [INLINES.HYPERLINK]: (node: any) => {
                  const { uri } = node?.data;
                  const content = node?.content?.[0];
                  if (content && "value" in content) {
                    return (
                      <a href={uri} target="_blank" rel="noopener noreferrer" onClick={handleExternalLinkClick}>
                        {content.value}
                      </a>
                    );
                  }
                  return null;
                },
              },
            })}
          </AccordionBody>
        ) : (
          <></>
        )}
      </BricksAccordionItem>
    </div>
  );
};

export default AccordionWrapper;
